import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { ImageModule } from '@df/module-image/image.module';
import { ColorOptions, RatioOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { defaultImagePng } from 'projects/app-default/src/app/icms.config';
import { InlineButtonComponent } from 'projects/app-default/src/app/ui/buttons/inline-button/inline-button.component';
import SwiperCore, { EffectCards, Swiper } from 'swiper';
import { SwiperModule } from 'swiper/angular';

SwiperCore.use([EffectCards]);

export namespace NCmsCollectionPromo {
    export interface IPageComponentCollectionPromoData extends NICmsContentComponent.IData {
        topText: string;
        titleColor?: string;
        backgroundImage: string;
        backgroundImageRatio: string;
        leftImage: string;
        leftImageRatio: string;
        shopCollectionLink: string;
        buttonText?: string;
        products?: number[];
        cards?: ICard[];
        autoplayDelay?: number;
        loop?: boolean;
    }

    export interface ICard {
        productVariant: boolean;
        productId?: number;
        cardImage?: string;
        cardTitle?: string;
        cta?: string;
        ctaLink?: string;
    }

    export interface ICardWithProduct extends ICard {
        product?: Product<IProductDataV2>;
    }
}

let uid = 0;
@ICmsComponent<PageComponentCollectionPromoComponent>({
    id: 'collection-promo',
    name: 'Collection Promo',
    fields: [
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'topText',
            title: 'Top Text',
            required: true,
            default: 'The Loire Collection'
        },
        <NICmsContentField.IChoiceField>{
            key: 'titleColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title Color',
            options: ColorOptions,
            previewMode: 'color',
            description: 'Color for the title text'
        },
        {
            key: 'backgroundImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Background Image',
            required: true,
            default: defaultImagePng
        },
        <NICmsContentField.IChoiceField>{
            key: 'backgroundImageRatio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Image Ratio',
            options: RatioOptions,
            required: true,
            default: 'ratio-16-9'
        },
        {
            key: 'leftImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Left Image',
            required: true,
            default: defaultImagePng
        },
        <NICmsContentField.IChoiceField>{
            key: 'leftImageRatio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Left Image Ratio',
            options: RatioOptions,
            required: true,
            default: 'ratio-1-1'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'shopCollectionLink',
            title: 'Shop Collection Link',
            required: true,
            default: '/'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'buttonText',
            title: 'Button Text'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'cards',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Cards',
            itemName: 'Card',
            itemNameField: 'card',
            maxSize: 4,
            fields: [
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'productVariant',
                    title: 'Product Variant',
                    default: false
                },
                <NICmsContentField.IRelationField>{
                    key: 'productId',
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Product',
                    options: {
                        title: 'Product',
                        type: 'product',
                        multiple: false,
                        ordered: true
                    },
                    required: data => data.productVariant,
                    canShow: (data: NCmsCollectionPromo.ICard) => data.productVariant === true
                },
                {
                    key: 'cardImage',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Card Image',
                    default: defaultImagePng,
                    required: data => !data.productVariant,
                    description: 'Will be visible if product variant is not selected or selected product has no image'
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'cardTitle',
                    title: 'Card Title',
                    default: 'Loire Fly Hood Benetton',
                    required: data => !data.productVariant,
                    canShow: (data: NCmsCollectionPromo.ICard) => data.productVariant === false
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'cta',
                    title: 'CTA',
                    default: 'shop now',
                    required: data => !data.productVariant,
                    canShow: (data: NCmsCollectionPromo.ICard) => data.productVariant === false
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'ctaLink',
                    title: 'CTA Link',
                    default: '/',
                    required: data => !data.productVariant,
                    canShow: (data: NCmsCollectionPromo.ICard) => data.productVariant === false
                }
            ]
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.number,
            key: 'autoplayDelay',
            title: 'Autoplay Delay',
            default: 4000
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'loop',
            title: 'Loop',
            default: false
        }
    ]
})
@Component({
    selector: 'collection-promo',
    templateUrl: 'collection-promo.html',
    styleUrls: ['collection-promo.scss'],
    standalone: true,
    imports: [NgClass, ImageModule, ICmsClientModule, DfUiModule, HrefComponent, TranslationModule, SwiperModule, InlineButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentCollectionPromoComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsCollectionPromo.IPageComponentCollectionPromoData;
    private productResource = inject(ProductResource);
    private swiper: Swiper | undefined;
    protected uid = uid++;
    protected loadedProducts: { [key: number]: Product<IProductDataV2> } = {};
    override canDetach = true;

    override detectChanges(): void {
        if (this.component?.cards?.length) {
            const productToLoad: number[] = this.component?.cards
                .filter(card => card.productVariant === true && !!card.productId && !this.loadedProducts[card.productId])
                .map(el => el.productId as number);

            if (productToLoad?.length) {
                this.productResource.loadAllIds(productToLoad, 2, { safe: true }).then(products => {
                    products.forEach(product => (this.loadedProducts[product.id] = product));
                    this.changeDetectorRef.detectChanges();
                });
            }
        }
        super.detectChanges();
    }

    protected getCardProductData(card: NCmsCollectionPromo.ICardWithProduct): IProductDataV2 | undefined {
        return card.productVariant && !!card?.productId ? this.loadedProducts[card.productId]?.data : undefined;
    }

    protected onSwiper(el: Swiper): void {
        this.swiper = el;
    }

    protected nextSlide(): void {
        this.swiper?.slideNext();
    }

    protected previousSlide(): void {
        this.swiper?.slidePrev();
    }

    protected get isBeggining(): boolean {
        return this.swiper?.isBeginning ?? true;
    }

    protected get isEnd(): boolean {
        return this.swiper?.isEnd ?? true;
    }
}
